import {configureApp} from '@/shared';
import {fromPairs} from 'lodash-es';
import {App, Component, createApp} from 'vue';

export interface AppOptions {
    i18n?: object;
    router?: object;
    provide?: Record<string, unknown>;
    propsData?: Record<string, unknown>;
}

type OptionsFactory = (element: Element) => AppOptions;

export class VueComponentInitializer {
    private static mountedApps: {element: HTMLElement; app: App}[] = [];

    private readonly selector: string;
    private readonly componentConstructor: Component;
    private readonly additionalOptionsFactory: OptionsFactory | undefined;
    private readonly additionalRenderOptions: object;

    public constructor(
        selector: string,
        component: Component,
        additionalOptionsFactory?: OptionsFactory,
        additionalRenderOptions: object = {},
        ) {
        this.selector = selector;
        this.componentConstructor = component;
        this.additionalOptionsFactory = additionalOptionsFactory;
        this.additionalRenderOptions = additionalRenderOptions;
    }

    public initialize(container: HTMLElement): void {
        VueComponentInitializer.unmountDisconnectedApps();

        container.querySelectorAll(this.selector).forEach(element => {
            const prefix = ':';

            const propPairs = element.getAttributeNames()
                .filter(attribute => attribute !== 'class' && attribute !== 'style' && attribute !== 'id')
                .map(attribute => attribute.startsWith(prefix)
                    ? [attribute.substring(prefix.length), JSON.parse(element.getAttribute(attribute) as string)]
                    : [attribute, element.getAttribute(attribute)]);

            const additionalOptions = this.additionalOptionsFactory === undefined
                ? {}
                : this.additionalOptionsFactory(element);

            const props = {...(additionalOptions.propsData || {}), ...fromPairs(propPairs), ...this.additionalRenderOptions};

            const app = createApp(this.componentConstructor, props);

            configureApp(app);
            this.applyOptions(app, additionalOptions);

            // Vue 3 app.mount() renders inside the element. Elements like <input>,
            // <textarea> and <select> cannot contain child HTML, so replace them with
            // a <div> first.
            let mountTarget = element;
            if (element.tagName === 'INPUT' || element.tagName === 'TEXTAREA' || element.tagName === 'SELECT') {
                const div = document.createElement('div');
                for (const cls of (element as HTMLElement).classList) {
                    div.classList.add(cls);
                }
                element.replaceWith(div);
                mountTarget = div;
            }

            app.mount(mountTarget);
            VueComponentInitializer.mountedApps.push({element: mountTarget as HTMLElement, app});
        });
    }

    private static unmountDisconnectedApps(): void {
        VueComponentInitializer.mountedApps = VueComponentInitializer.mountedApps.filter(entry => {
            if (!entry.element.isConnected) {
                entry.app.unmount();
                return false;
            }
            return true;
        });
    }

    private applyOptions(app: App, options: AppOptions): void {
        if (options.i18n) {
            app.use(options.i18n as any);
        }
        if (options.router) {
            app.use(options.router as any);
        }
        if (options.provide) {
            for (const [key, value] of Object.entries(options.provide)) {
                app.provide(key, value);
            }
        }
    }
}
